import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

// ---- Org config (settings UI) ----

export type ConfigSettingMeta = {
  key: string;
  group: string;
  description: string;
  type: "string" | "int" | "bool" | "path" | "secret" | "csv" | "tuple";
  env_var: string;
  secret: boolean;
  org_scoped: boolean;
  // Optional registry-defined constraints. ``null`` when the
  // entry doesn't pin a particular bound. Surfaced so the field
  // can render HTML5 ``min`` / ``max`` / ``maxLength`` /
  // ``pattern`` attrs and helper text.
  min: number | null;
  max: number | null;
  pattern: string | null;
  max_length: number | null;
  /** Only the org's admins may change it; a manager sees it read-only.
   *  Optional so an older backend reads as "anyone who may edit". */
  admin_only?: boolean;
};

export type ConfigSettingValue = {
  meta: ConfigSettingMeta;
  // ``null`` for secrets even when set (the server never returns
  // plaintext). Use ``is_set`` to know whether a value exists.
  value: unknown;
  source: "env" | "org" | "root" | "default";
  is_pinned: boolean;
  is_set: boolean;
};

export type GroupedSettings = {
  group: string;
  settings: ConfigSettingValue[];
};

export type OrgConfigResponse = {
  org_slug: string;
  org_name: string;
  is_root: boolean;
  groups: GroupedSettings[];
};

const orgConfigKey = (slug: string) =>
  ["orgs", slug, "config"] as const;

export function useOrgConfig(slug: string) {
  return useQuery({
    queryKey: orgConfigKey(slug),
    queryFn: async () =>
      (await api.get<OrgConfigResponse>(`/orgs/${slug}/config`)).data,
    enabled: Boolean(slug),
    staleTime: 30_000,
  });
}

/** Read-only superadmin view of system-scoped settings.
 *  Endpoint returns a flat list (system_router doesn't group); we
 *  rebuild the GroupedSettings shape on the client so the existing
 *  rendering primitives can be reused. */
const systemConfigKey = ["system", "config"] as const;

export function useSystemConfig() {
  return useQuery({
    queryKey: systemConfigKey,
    queryFn: async () => {
      const r = await api.get<ConfigSettingValue[]>("/system/config");
      // Group while preserving server-side declaration order.
      const seen: string[] = [];
      const buckets: Record<string, ConfigSettingValue[]> = {};
      for (const s of r.data) {
        if (!(s.meta.group in buckets)) {
          buckets[s.meta.group] = [];
          seen.push(s.meta.group);
        }
        (buckets[s.meta.group] as ConfigSettingValue[]).push(s);
      }
      return seen.map((g) => ({
        group: g,
        settings: (buckets[g] ?? []) as ConfigSettingValue[],
      }));
    },
    staleTime: 30_000,
  });
}

/** Deployment-level security warnings for the System tab (superadmin). */
export type SystemWarning = { id: string; org_count: number | null };

export function useSystemWarnings() {
  return useQuery({
    queryKey: ["system", "warnings"] as const,
    queryFn: async () => (await api.get<SystemWarning[]>("/system/warnings")).data,
    staleTime: 60_000,
  });
}

export function useSetOrgConfig(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { key: string; value: unknown }) => {
      const r = await api.put<ConfigSettingValue>(
        `/orgs/${slug}/config/${encodeURIComponent(payload.key)}`,
        { value: payload.value },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgConfigKey(slug) });
    },
  });
}

export function useDeleteOrgConfig(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      await api.delete(
        `/orgs/${slug}/config/${encodeURIComponent(key)}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orgConfigKey(slug) });
    },
  });
}
